import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Layers, CreditCard, Package } from 'lucide-react';
import './CardStack.css';

// Utility component for highlighted text within cards
export const Highlight = ({ children, className }) => {
    return (
        <span className={`highlight ${className || ''}`}>
            {children}
        </span>
    );
};

const CardStack = ({ items = [] }) => {
    const [activeIndex, setActiveIndex] = useState(0);

    // Auto-rotate logic
    useEffect(() => {
        if (items.length <= 1) return;

        const interval = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % items.length);
        }, 3500); // 3.5 seconds per card

        return () => clearInterval(interval);
    }, [items.length]);

    return (
        <div className="card-stack-container">
            {items.map((item, index) => {
                // Calculate display order relative to the active card
                // This logic ensures a smooth infinite loop transition
                const displayOffset = (index - activeIndex + items.length) % items.length;

                const isTopCard = displayOffset === 0;

                // We only render the top 3 cards to prevent visual clutter
                if (displayOffset > 2) return null;

                return (
                    <motion.div
                        key={item.id}
                        className="card-stack-item"
                        initial={false}
                        animate={{
                            // Increased offset: -35px per card
                            top: displayOffset * -35,
                            // Aggressive scale: 1, 0.92, 0.84
                            scale: 1 - displayOffset * 0.08,
                            // Dynamic Z-index so higher displayOffset is "further back"
                            zIndex: items.length - displayOffset,
                            // Fades out the background cards slightly
                            opacity: 1 - displayOffset * 0.2,
                            // Subtle 3D tilt
                            rotateX: displayOffset * -3,
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                        }}
                        whileHover={{
                            // Only the top card jumps up on hover
                            y: isTopCard ? -15 : 0,
                            transition: { duration: 0.2 }
                        }}
                        onClick={() => setActiveIndex(index)}
                    >
                        <div className="card-header">
                            <div className="icon-container">
                                {item.icon && <item.icon className="card-icon" />}
                            </div>
                            <div className="text-content">
                                <h3>{item.name}</h3>
                                <p>{item.designation}</p>
                            </div>
                        </div>
                        <div className="card-content">
                            {item.content}
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

export default CardStack;